// Lightbox controller for displaying images and videos in an overlay
// Example usage:
// <a href="/image.jpg" data-controller="lightbox" data-lightbox-type-value="image" data-lightbox-url-value="/image.jpg">Open Image</a>
// <video src="/video.mp4" data-controller="lightbox" data-lightbox-type-value="video" data-lightbox-url-value="/video.mp4">Open Video</video>

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    type: String,
    title: { type: String, default: "" }
  }

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.previouslyFocusedElement = null
  }

  open(event) {
    event.preventDefault()
    this.previouslyFocusedElement = document.activeElement
    this.items = Array.from(document.querySelectorAll('[data-controller~="lightbox"]'))
    this.index = this.items.indexOf(event.currentTarget || this.element)
    this.showOverlayForIndex(this.index)
  }

  showOverlayForIndex(index) {
    this.index = index
    const target = this.items[this.index]
    const url = target.dataset.lightboxUrl || target.href
    const type = target.dataset.lightboxType || 'image'
    const title = target.dataset.lightboxTitle || target.title || target.alt || ''

    if (this.overlay) {
      this.updateContent(url, type, title)
    } else {
      this.showOverlay(url, type, title)
    }
  }

  showOverlay(url, type, title) {
    this.close() // ensure only one overlay

    this.overlay = document.createElement("div")
    this.overlay.className = "fixed inset-0 bg-black/80 flex items-center justify-center z-[70]"
    this.overlay.setAttribute("role", "dialog")
    this.overlay.setAttribute("aria-modal", "true")
    this.overlay.setAttribute("aria-label", `Lightbox - ${title || `${type} viewer`}`)
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close()
    })

    // Create content container
    const contentContainer = document.createElement("div")
    contentContainer.className = "relative max-w-full max-h-full"
    contentContainer.setAttribute("role", "document")

    // Create content
    let content
    if (type === "video") {
      content = document.createElement("video")
      content.src = url
      content.controls = true
      content.setAttribute("aria-label", title || "Video content")
    } else {
      content = document.createElement("img")
      content.src = url
      content.alt = title || "Image content"
    }
    content.className = "max-w-full max-h-full"
    contentContainer.appendChild(content)

    // Create navigation controls
    const controls = this.createNavigationControls()
    contentContainer.appendChild(controls)

    // Create close button
    const closeButton = this.createCloseButton()
    contentContainer.appendChild(closeButton)

    // Create status text for screen readers
    const statusText = document.createElement("div")
    statusText.className = "sr-only"
    statusText.setAttribute("aria-live", "polite")
    statusText.setAttribute("aria-atomic", "true")
    statusText.id = "lightbox-status"
    contentContainer.appendChild(statusText)

    this.overlay.appendChild(contentContainer)
    document.body.appendChild(this.overlay)

    // Announce to screen readers
    this.announceStatus()

    // Set up event listeners
    document.addEventListener("keydown", this.handleKeydown)

    // Prevent scrolling on the underlying page
    document.body.style.overflow = "hidden"

    // Hide background content from screen readers
    this.hideBackgroundContent()

    // Focus management
    this.content = content
    this.focusTrap = this.createFocusTrap()
    this.focusTrap.focusFirstElement()

    // Store references
    this.statusText = statusText
    this.closeButton = closeButton
  }

  createNavigationControls() {
    const controls = document.createElement("div")
    controls.className = "absolute inset-0 pointer-events-none flex items-center justify-between p-4"

    // Previous button
    const prevButton = document.createElement("button")
    prevButton.className = "pointer-events-auto bg-black/50 text-white p-2 rounded-full hover:bg-black/70 focus:outline-hidden focus:ring-2 focus:ring-white"
    prevButton.innerHTML = "‹"
    prevButton.setAttribute("aria-label", "Previous item")
    prevButton.addEventListener("click", () => this.previous())
    controls.appendChild(prevButton)

    // Next button
    const nextButton = document.createElement("button")
    nextButton.className = "pointer-events-auto bg-black/50 text-white p-2 rounded-full hover:bg-black/70 focus:outline-hidden focus:ring-2 focus:ring-white"
    nextButton.innerHTML = "›"
    nextButton.setAttribute("aria-label", "Next item")
    nextButton.addEventListener("click", () => this.next())
    controls.appendChild(nextButton)

    this.prevButton = prevButton
    this.nextButton = nextButton
    return controls
  }

  createCloseButton() {
    const closeButton = document.createElement("button")
    closeButton.className = "absolute top-2 right-2 bg-black/50 text-white px-3 py-1 rounded-full hover:bg-black/70 focus:outline-hidden focus:ring-2 focus:ring-white"
    closeButton.innerHTML = "×"
    closeButton.setAttribute("aria-label", "Close lightbox")
    closeButton.addEventListener("click", () => this.close())
    return closeButton
  }

  createFocusTrap() {
    const focusableElements = [
      this.closeButton,
      this.prevButton,
      this.nextButton,
      this.content
    ].filter(Boolean)

    return {
      focusFirstElement: () => {
        if (focusableElements.length > 0) {
          focusableElements[0].focus()
        }
      },
      focusLastElement: () => {
        if (focusableElements.length > 0) {
          focusableElements[focusableElements.length - 1].focus()
        }
      },
      handleTab: (event) => {
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    }
  }

  updateContent(url, type, title) {
    if (!this.overlay) {
      this.showOverlay(url, type, title)
      return
    }

    const contentContainer = this.overlay.querySelector('[role="document"]')
    const oldContent = contentContainer.querySelector('img, video')
    if (oldContent) oldContent.remove()

    let content
    if (type === "video") {
      content = document.createElement("video")
      content.src = url
      content.controls = true
      content.setAttribute("aria-label", title || "Video content")
    } else {
      content = document.createElement("img")
      content.src = url
      content.alt = title || "Image content"
    }
    content.className = "max-w-full max-h-full"
    contentContainer.insertBefore(content, contentContainer.firstChild)

    this.content = content
    this.announceStatus()
  }

  announceStatus() {
    if (this.statusText && this.items) {
      const current = this.index + 1
      const total = this.items.length
      const target = this.items[this.index]
      const title = target.dataset.lightboxTitle || target.title || target.alt || ''
      const type = target.dataset.lightboxType || 'image'

      this.statusText.textContent = `${title || type} ${current} of ${total}`
    }
  }

  hideBackgroundContent() {
    // Hide all content outside the lightbox from screen readers
    const elementsToHide = document.querySelectorAll('body > *:not([role="dialog"])')
    this.hiddenElements = []

    elementsToHide.forEach(element => {
      if (element.getAttribute('aria-hidden') !== 'true') {
        this.hiddenElements.push(element)
        element.setAttribute('aria-hidden', 'true')
      }
    })
  }

  showBackgroundContent() {
    // Restore aria-hidden attributes
    if (this.hiddenElements) {
      this.hiddenElements.forEach(element => {
        element.removeAttribute('aria-hidden')
      })
      this.hiddenElements = []
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
    } else if (event.key === "Tab") {
      this.focusTrap?.handleTab(event)
    } else {
      // Prevent all other keyboard input
      event.preventDefault()
    }
  }

  next() {
    if (!this.items) return
    this.index = (this.index + 1) % this.items.length
    this.showOverlayForIndex(this.index)
  }

  previous() {
    if (!this.items) return
    this.index = (this.index - 1 + this.items.length) % this.items.length
    this.showOverlayForIndex(this.index)
  }

  close() {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
      document.removeEventListener("keydown", this.handleKeydown)
    }

    // Re-enable scrolling on the underlying page
    document.body.style.overflow = "auto"

    // Show background content again
    this.showBackgroundContent()

    // Restore focus to previously focused element
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }
  }

  disconnect() {
    this.close()
  }
}
